import { useMutation, useQuery } from '@tanstack/react-query';
import { db } from '@util/firebase';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import {
  collection,
  doc,
  getDocs,
  limit as limitTo,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { z } from 'zod';

/**
 * Backstop on a single status's worth of requests. Pending, the largest and the
 * default, holds about 2,400 today, so nothing is cut off now.
 */
export const CANCELLATIONS_PAGE_LIMIT = 3000;

export enum CANCELLATION_STATUS {
  CANCELLED = 'cancelled',
  DENIED = 'denied',
  PENDING = 'pending',
  REFUNDED = 'refunded',
  UPGRADED = 'upgraded',
}

const cancellationRequestSchema = z.object({
  amount_charged: z.number(),
  buyer_id: z.string(),
  chat_id: z.string(),
  created: z.number(),
  id: z.string(),
  order_id: z.string(),
  product_id: z.string(),
  reason: z.string(),
  requested_by: z.literal('buyer').or(z.literal('seller')).default('seller'),
  seller_id: z.string(),
  status: z.nativeEnum(CANCELLATION_STATUS),
});

export type CancellationRequest = z.infer<typeof cancellationRequestSchema>;

export const useCancellationRequests = ({
  status,
  order_id,
  seller_id,
  buyer_id,
  max,
}: {
  status?: CANCELLATION_STATUS;
  order_id?: string;
  seller_id?: string;
  buyer_id?: string;
  /**
   * Caps an otherwise unbounded read, newest first. Only the support lists pass
   * it: the calls scoped to one order or one user are already small, and adding
   * an ordering to those would need a composite index apiece.
   */
  max?: number;
} = {}) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const { userDoc } = useAuth();
  const data = useQuery({
    queryKey: [
      'cancellation-requests',
      status,
      order_id,
      seller_id,
      buyer_id,
      max,
    ],
    queryFn: async () => {
      const q = query(
        collection(db, 'cancellation_requests'),
        ...(status ? [where('status', '==', status)] : []),
        ...(order_id ? [where('order_id', '==', order_id)] : []),
        ...(seller_id ? [where('seller_id', '==', seller_id)] : []),
        ...(buyer_id ? [where('buyer_id', '==', buyer_id)] : []),
        ...(max ? [orderBy('created', 'desc'), limitTo(max)] : [])
      );
      const snap = await getDocs(q);
      return snap.docs.map((doc) => doc.data() as CancellationRequest);
    },
    enabled: !!userDoc?.roles?.support || !!seller_id || !!buyer_id,
  });

  const updateCancellationRequest = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: CANCELLATION_STATUS;
    }) => {
      const ref = doc(db, 'cancellation_requests', id);
      await updateDoc(ref, { status });
    },
    onSuccess: () => {
      data.refetch();
      showSuccessToast('Cancellation request updated');
    },
    onError: (error) => {
      console.error(error);
      showErrorToast('Error updating cancellation request: ' + error.message);
    },
  });

  const denyCancellationRequest = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const ref = doc(db, 'cancellation_requests', id);
      await updateDoc(ref, { status: CANCELLATION_STATUS.DENIED });
    },
    onSuccess: () => {
      data.refetch();
      showSuccessToast('Cancellation request denied');
    },
    onError: (error) => {
      console.error(error);
      showErrorToast('Error denying cancellation request: ' + error.message);
    },
  });

  return {
    data,
    updateCancellationRequest,
    denyCancellationRequest,
    isLoading: data.isLoading,
  };
};
